import { t } from "@lingui/macro";
import { useAtom } from "jotai";

import { BookingWizardDataAtom } from "@/features/BookingWizard/atoms/stores";
import { useGetCourseLayoutOptions } from "@/features/BookingWizard/hooks/useGetCourseLayoutOptions/useGetCourseLayoutOptions";
import { useGetLayoutHoleCountString } from "@/features/BookingWizard/hooks/useGetLayoutHoleCountString/useGetLayoutHoleCountString";
import { SelectableTile } from "@whoosh/design-system/src/components/controls/SelectableTile/SelectableTile";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

type Props = {
  facility: any;
};

/**
 * Component for selecting the number of holes in a golf booking
 */
export const HolesControl = ({ facility }: Props) => {
  const [bookingWizardData, setBookingWizardData] = useAtom(
    BookingWizardDataAtom
  );

  // Use the custom hook to get course layout options
  const courseLayoutOptions = useGetCourseLayoutOptions(facility);

  // Use the hook for formatting hole count strings
  const getLayoutHoleCountString = useGetLayoutHoleCountString();

  // Only show for request bookings
  if (bookingWizardData?.type !== "RequestBooking") {
    return null;
  }

  const courseLayoutId =
    bookingWizardData.courseLayoutId ||
    courseLayoutOptions[courseLayoutOptions.length - 1]?.id ||
    "";

  if (courseLayoutOptions.length === 0) {
    return null;
  }

  return (
    <VStack gap={4} p={5} bg="surface-primary" borderRadius="4" mb={2}>
      <Text variant="mainSmallSemiBold">{t`Holes`}</Text>
      <HStack gap={2}>
        {courseLayoutOptions.map((layout) => (
          <SelectableTile
            flexBasis={1}
            key={layout.id}
            setSelected={() => {
              setBookingWizardData({
                ...bookingWizardData,
                courseLayoutId: layout.id,
                courseLayoutHoleCount: layout.holeCount,
              });
            }}
            title={getLayoutHoleCountString(layout.code, layout.holeCount)}
            isSelected={layout.id === courseLayoutId}
          />
        ))}
      </HStack>
    </VStack>
  );
};
